import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { openDialog } from '../../base/dialog';
import { IconChatLock, IconChatUnlock, IconCloseCircle, IconRaisedHand, IconRaisedHandSlash, IconUserGroup } from '../../base/icons';
import { blockVisitorRaiseHand, kickVisitor, suspendVisitorChat } from '../../visitors/actions';
import { getVisitorsRoster } from '../../visitors/functions';
import { ParticipantContextMenu } from './ParticipantContextMenu';
import VisitorPromotionDialog from './VisitorPromotionDialog';
import { ContextMenuIcon, ContextMenuItem, ContextMenuItemGroup } from './styled';
const MenuItem = styled(ContextMenuItem).attrs({
    as: 'button',
    type: 'button'
}) `
    background: none;
    border: 0;
    color: inherit;
    font: inherit;
    text-align: left;
    width: 100%;
`;
/**
 * Visitor-specific controls in the participant pane's shared context menu.
 *
 * @param {IProps} props - Menu anchor, visitor identifier and close callback.
 * @returns {ReactNode}
 */
export default function VisitorContextMenu({ offsetTarget, onSelect, vid }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const visitor = useSelector(getVisitorsRoster).find(v => v.vid === vid);
    const chatSuspended = useSelector((state) => { var _a; return Boolean((_a = state['features/visitors'].suspendedChatVids) === null || _a === void 0 ? void 0 : _a[vid]); });
    const runAction = useCallback((action) => () => {
        onSelect();
        dispatch(action);
    }, [dispatch, onSelect]);
    if (!visitor) {
        return null;
    }
    const options = [
        {
            action: openDialog(VisitorPromotionDialog, {
                request: {
                    from: visitor.jid,
                    nick: visitor.nick || t('webinar.backstageUnnamed')
                }
            }),
            icon: IconUserGroup,
            label: 'participantsPane.actions.promote'
        },
        {
            action: suspendVisitorChat(vid, !chatSuspended),
            icon: chatSuspended ? IconChatUnlock : IconChatLock,
            label: chatSuspended
                ? 'chat.resumeVisitorChat' : 'chat.suspendVisitorChat'
        },
        {
            action: blockVisitorRaiseHand(vid, !visitor.handBlocked),
            icon: visitor.handBlocked ? IconRaisedHand : IconRaisedHandSlash,
            label: visitor.handBlocked
                ? 'participantsPane.actions.allowRaiseHand'
                : 'participantsPane.actions.blockRaiseHand'
        },
        {
            action: kickVisitor(vid),
            icon: IconCloseCircle,
            label: 'participantsPane.actions.kick'
        }
    ];
    return (React.createElement(ParticipantContextMenu, { offsetTarget: offsetTarget, onLeave: onSelect, onSelect: onSelect },
        React.createElement(ContextMenuItemGroup, null, options.map(option => (React.createElement(MenuItem, { key: option.label, onClick: runAction(option.action) },
            React.createElement(ContextMenuIcon, { src: option.icon }),
            React.createElement("span", null, t(option.label))))))));
}
